import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { push } from "redux-first-history";
import toast from "react-hot-toast";

import payments from "../../redux/actions/payments";
import { getBalance } from "../../redux/actions/session";
import { hapticSuccess } from "../../utils/haptic";

import Menu from "../../components/menuDesktop/MenuDesktop";
import MobileMenu from "../../components/menuMobile/MenuMobile";
import CurrencyInput from "../currencyInput/CurrencyInput";

import "../../pages/cpfRegistration/CpfRegistration.scss";

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

const Payment = () => {
  const token = useSelector((state) => state.session.token);
  const payment = useSelector((state) => state.payment);
  const cpf = useSelector((state) => state.session.cpf);
  const email = useSelector((state) => state.session.email);
  const balance = useSelector((state) => state.session.balance);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [amount, setAmount] = useState("");
  const initialBalanceRef = useRef(null);
  const confirmedRef = useRef(false);

  useEffect(() => {
    dispatch(getBalance());
  }, [dispatch]);

  useEffect(() => {
    if (!email) return;
    if (token && !cpf) {
      dispatch(push("/cpf?redirect=payment"));
    }
  }, [token, cpf, email, dispatch]);

  // Auto-detecta pagamento: enquanto o QR estiver aberto, faz polling do saldo
  useEffect(() => {
    if (!payment.pixQrCode) {
      initialBalanceRef.current = null;
      confirmedRef.current = false;
      return;
    }

    initialBalanceRef.current = balance ?? 0;
    confirmedRef.current = false;

    const pollId = setInterval(() => dispatch(getBalance()), POLL_INTERVAL_MS);
    const timeoutId = setTimeout(() => clearInterval(pollId), POLL_TIMEOUT_MS);

    return () => {
      clearInterval(pollId);
      clearTimeout(timeoutId);
    };
  }, [payment.pixQrCode, dispatch]);

  // Detecta aumento de saldo → pagamento confirmado
  useEffect(() => {
    if (!payment.pixQrCode) return;
    if (confirmedRef.current) return;
    if (initialBalanceRef.current === null) return;
    if (balance > initialBalanceRef.current) {
      confirmedRef.current = true;
      hapticSuccess();
      toast.success("Pagamento confirmado! Saldo atualizado.");
      navigate("/");
    }
  }, [balance, payment.pixQrCode, navigate]);

  if (!token) return null;

  const handleClose = () => navigate("/");

  const handleSubmit = (e) => {
    e.preventDefault();
    const formattedAmount = amount
      .trim()
      .replace("R$", "")
      .replace(",", ".");

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        purchase: {
          actionField: { revenue: formattedAmount },
        },
      },
    });
    dispatch(payments.depositCash(formattedAmount));
  };

  const copyPix = () => {
    if (!payment.pixCopiaCola) return;
    navigator.clipboard.writeText(payment.pixCopiaCola);
    toast.success("Código Pix copiado com sucesso!");
  };

  const getQrSrc = () => {
    if (!payment.pixQrCode) return "";
    return payment.pixQrCode.startsWith("http")
      ? payment.pixQrCode
      : `data:image/jpeg;base64,${payment.pixQrCode}`;
  };

  return (
    <>
      <Menu />

      <div
        className="tx-modal-overlay"
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        {!payment.pixQrCode ? (
          <form className="tx-modal" onSubmit={handleSubmit} noValidate>
            <header className="tx-modal__header">
              <h2 className="tx-modal__title">Depositar</h2>
              <button
                type="button"
                className="tx-modal__close"
                onClick={handleClose}
                aria-label="Fechar"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </header>

            <p className="tx-modal__description">Quanto você quer depositar?</p>

            <label className="tx-field">
              <span className="tx-field__label">Valor do depósito</span>
              <CurrencyInput
                amount={amount}
                setAmount={setAmount}
                className="tx-field__input"
              />
            </label>

            <button
              type="submit"
              className="tx-modal__submit"
              disabled={
                !amount ||
                parseFloat(amount.replace(/R\$/g, "").replace(",", ".")) <= 0
              }
            >
              Depositar
            </button>
          </form>
        ) : (
          <div className="tx-modal">
            <header className="tx-modal__header">
              <h2 className="tx-modal__title">Pix</h2>
              <button
                type="button"
                className="tx-modal__close"
                onClick={handleClose}
                aria-label="Fechar"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </header>

            <p className="tx-modal__description">
              Pague o pix para realizar o depósito.
            </p>
            <p className="tx-modal__hint">
              Após fazer seu pagamento, seu saldo irá ser depositado
              automaticamente. Qualquer dúvida, fale com a gente pelo whatsapp.
            </p>

            <div className="tx-modal__qr">
              <img src={getQrSrc()} alt="QR Code Pix" />
            </div>

            <div className="tx-field">
              <span className="tx-field__label">Pix copia e cola</span>
              <div className="tx-modal__pix-code">{payment.pixCopiaCola}</div>
            </div>

            <button
              type="button"
              className="tx-modal__submit"
              onClick={copyPix}
            >
              Copiar código Pix
            </button>
          </div>
        )}
      </div>

      <MobileMenu />
    </>
  );
};

export default Payment;
